import * as React from 'react'
import constants from '../constants'
import Fallback from '../components/Fallback'
import * as styles from './Storefront.module.scss'
import Content from '../components/Content'
import fetch from '../utils/fetch'
import { isChannelUpdated, getLiveChannelUpdateURL } from '../utils/liveChannelRefresh'
import perlBoolean from '../utils/perlBoolean'
import * as cn from 'classnames'
import * as csm from '../utils/csm'
import qString from '../utils/queryString'
import { InitialArgs, StorefrontItem, Collection, StorefrontOutput, StorefrontItemPlaybackAction, WPSClient, Args } from 'DVWebNodeUtils';
import { PaginationProps } from '../components/Pagination';


export interface PlayerCloseOpt {
  //This is incomplete, and declares only the parts needed for our current use case.
  videoType: string
  asin: string
}


declare let window: Window & {
  ue: { count: (counter: string, value: number) => void; }
  ues: any
  uet: any
  P: any
}

const HORIZONTAL_ENRICH_THRESHOLD = 10
const OFF_WHITE = '#F2F4F6' // from DVUI Style GUIDE

const LOGGING_PREFIX = 'DVWeb::Storefront'

export interface StorefrontStateItem extends StorefrontItem {
  isDisabled?: boolean
  isRemoved?: boolean
}

export interface StorefrontStateCollection extends Collection {
  items: StorefrontStateItem[]
}

export interface StorefrontState extends StorefrontOutput, Args {
  request?: InitialArgs
  asyncCallsScopes?: string[]
  isLoading?: boolean
  isFrozen?: boolean
  featureSwitches: FeatureSwitches
  showBlueBars?: boolean
  storefrontPlaybackActive?: boolean
  originalPlayableTitle?: string
  canLivePlayback: boolean
  collections: StorefrontStateCollection[]
  pagination?: PaginationProps
}

export interface FeatureSwitches {
  [feature: string]: boolean
}

class Storefront extends React.Component<StorefrontOutput & Args, StorefrontState> {
  state: StorefrontState
  isLoadingFilteredContent: boolean
  isLoadingMoreContent: boolean
  isPlaying: boolean
  channelItems: StorefrontItem[]
  itemsPlayableStatus: { [x: string]: { playbackAction: StorefrontItemPlaybackAction }; }
  hasAltTheme: boolean
  hasRequiredPolyfills: boolean

  constructor(props: StorefrontOutput & Args) {
    super(props)
    this.state = {
      ...props, showBlueBars: false, canLivePlayback: true,
      storefrontPlaybackActive: Boolean(props.featureSwitches &&
        props.featureSwitches.lazyloadPB)
    }
    this.itemsPlayableStatus = {}
    this.hasAltTheme = perlBoolean(props.hasAltTheme)
    this.isLoadingFilteredContent = false
    this.isLoadingMoreContent = false
    this.isPlaying = false

    if (this.state && this.state && this.state.collections) {
      this.channelItems = this.state.collections
        .map(v => v.items)
        .reduce((acc, cur) => acc.concat(cur), [])
        .filter(i => i.hasOwnProperty('channelId'))
    }
    this.ensurePolyfilled()
    this.enrichSingleItem = this.enrichSingleItem.bind(this)
  }

  logError(error: Error, message: string, counter: string) {
    csm.count(counter)
    csm.logError(error, `[${LOGGING_PREFIX}] ${message}`)
  }

  isRequestSuccessful() {
    return this.state && !this.state.hasFailed
  }

  isUsingMock() {
    return Boolean(this.state.request &&
      this.state.request.context &&
      this.state.request.context.parameters &&
      this.state.request.context.parameters.mock
    )
  }

  fetch(url: string, scope: string = null, method: string = null, body: any = null) {
    if (scope) {
      if (window.ues) {
        window.ues(csm.SCOPES.TIME_ZERO, scope, new Date())
      }
    }
    return fetch(url, this.isUsingMock(), method, body).then((response: { [key: string]: any }) => {
      if (scope) {
        if (window.ue && window.uet) {
          window.ue.count(`DVWeb::${scope}`, 1)
          window.uet('bb', scope, csm.options)
        }
        response.scope = scope
      }

      return response
    })
  }

  componentDidCatch(error: Error) {
    this.logError(error,
      'Error while trying to render dvweb Storefront',
      `${LOGGING_PREFIX}::JSFatal::StorefrontLoad`)
    this.setState({ hasFailed: true })
  }

  getAsyncCallsScopes(scope: string) {
    const asyncCallsScopes = this.state.asyncCallsScopes || []
    return asyncCallsScopes.concat([scope])
  }

  startPaginationLoading() {
    this.isLoadingMoreContent = true
    if (this.state.pagination) {
      this.setState({
        pagination: {
          ...this.state.pagination,
          isLoading: true
        }
      })
    }
  }

  loadMoreContent() {
    const pageNumber = this.state.pagination.page
    const scope = `storefront-pagination-${pageNumber}`
    if (window.uet) {
      window.uet(csm.SCOPES.TIME_TO_CLICK, scope)
    }

    if (this.isLoadingMoreContent) {
      return
    }
    this.startPaginationLoading()

    this.fetch(this.state.pagination.apiUrl, scope).then((response: { [key: string]: any }) => {
      const collections = this.state.collections.concat(response.collections)
      const pagination = {
        ...response.pagination,
        isLoading: false
      }
      this.setState({
        ...this.state,
        collections,
        pagination,
        asyncCallsScopes: this.getAsyncCallsScopes(response.scope)
      }, () => {
        this.isLoadingMoreContent = false
      })
    }).catch((error: Error) => {
      this.logError(error,
        `Error while loading next page (${pageNumber})`,
        `${LOGGING_PREFIX}::JSError::InfiniteScroll`)
    })
  }

  getUpdatedFacetedNav(id: number) {
    const facetedNav = this.state.facetedNav
    const values = facetedNav.filter.values.map((value, i) => ({
      ...value,
      isCurrentlyApplied: i === id
    }))
    return {
      ...facetedNav,
      filter: {
        ...facetedNav.filter,
        values
      }
    }
  }

  setLoadingFilter(id: number) {
    this.isLoadingFilteredContent = true
    this.setState({
      facetedNav: this.getUpdatedFacetedNav(id),
      isLoading: true
    })
  }

  refreshPage() {
    window.scrollTo(0, 0)
    this.setState({isLoading: true})
    const client = new WPSClient()
    client.Storefront({
      context: this.props.context,
      ...this.props.pageRefreshParams
    })
    .then((response: { [key: string]: any }) => {
      this.setState({
        collections: response.collections,
        pagination: response.pagination,
        isLoading: false
      }, () => {
        this.enrich()
      })
    })
    .catch((error: Error) => {
      this.props.logger.error(error, 'Could not refresh Storefront page.', error)
      this.setState({isLoading: false})
  })
  }

  loadFilteredContent(id: number, apiUrl: string) {
    if (this.isLoadingFilteredContent) {
      return Promise.resolve()
    }

    this.setLoadingFilter(id)
    const filterName = this.state.facetedNav.filter.values[id].name
    const scope = `storefront-filter-${filterName}`
    return this.fetch(apiUrl, scope)
      .then((response: { [key: string]: any }) => {
        this.setState({
          collections: response.collections,
          pagination: response.pagination,
          epgIngress: response.epgIngress,
          isLoading: false,
          asyncCallsScopes: this.getAsyncCallsScopes(response.scope)
        }, () => {
          this.isLoadingFilteredContent = false
          this.enrich()
        })
      })
      .catch((error: Error) => {
        this.logError(error,
          `Error while trying to change filter to ${filterName}`,
          `${LOGGING_PREFIX}::JSError::ChangeFilter`)
          this.setState({isLoading: false})
      })
  }

  ensurePolyfilled() {
    if (!global.__SERVER__ && typeof IntersectionObserver == 'undefined' && !this.hasRequiredPolyfills) {
      require('intersection-observer')
      this.hasRequiredPolyfills = true
    }
  }

  componentDidMount() {
    const queryParams = qString(window.location.href)
    window.onpopstate = (event) => {
      event.preventDefault()

      if (event && event.state) {
        this.updateContentFromFilter(event.state)
      }
    }

    if (window.document.cookie.indexOf('amafeature=') > 0) {
      this.setState({ showBlueBars: true })
    }
    this.enrich()

    if (typeof window !== 'undefined' && typeof window.P !== 'undefined') {
      window.P.when('dv-player-integration').execute((player: any) => {
        player.on('play', () => {
          this.isPlaying = true
        })
        player.on('closed', (playbackOpt: PlayerCloseOpt) => {
          this.isPlaying = false
          if (this.props.pageRefreshParams) {
            this.refreshPage()
          } else if (this.state.storefrontPlaybackActive && playbackOpt && playbackOpt.asin &&
            playbackOpt.videoType !== 'Live') {
            this.refreshItem(this.state.originalPlayableTitle || playbackOpt.asin)
              .then((responseUpdate) => {
                this.setState({ ...responseUpdate, originalPlayableTitle: null })
              }).catch((error: Error) => {
                this.logError(error,
                  'Error while refreshing item after playback',
                  `${LOGGING_PREFIX}::JSError::RefreshItem`)
              })
          }
        })
        player.on('asinChanged', (playbackOpt: PlayerCloseOpt) => {
          if (playbackOpt && playbackOpt.asin && !this.state.originalPlayableTitle) {
            this.setState({ originalPlayableTitle: playbackOpt.asin })
          }
        })
      })
    }

    this.updateStateWithLivePlayback()
    if (queryParams && queryParams['av-search-asin'] &&
      queryParams['av-search-asin'] !== '' && queryParams['qid'] &&
      queryParams['qid'] !== '' && queryParams['sr'] && queryParams['sr'] !== '') {
      this.attributeSearchAsinMetrics(queryParams['av-search-asin'],
        queryParams['sr'], queryParams['qid'])
    }

    if (this.channelItems && this.channelItems.length > 0) {
      setInterval(() => {
        if (!this.isPlaying) {
          this.refreshLiveChannels()
        }
      }, constants.liveChannelsRefresh.REFRESH_INTERVAL)
    }
    if (this.hasAltTheme) {
      document.body.style.backgroundColor = OFF_WHITE
    }
  }

  updateContentFromFilter(stateObj: { i: number; apiUrl: string; }) {
    const { i, apiUrl } = stateObj

    if (typeof i !== 'undefined' && apiUrl) {
      this.loadFilteredContent(i, apiUrl)
    }
  }

  attributeSearchAsinMetrics(asin: string, searchRelations: string, searchQueryId: string) {
    if (this.isUsingMock()) {
      return
    }

    const paramString = `?type=${constants.SEARCH_ASIN_TYPE}` +
      `&asin=${asin}&sr=${searchRelations}&qid=${searchQueryId}`,
      url = `${constants.SEARCH_ASIN_METRIC_ENDPOINT}${paramString}`

    fetch(url)
      .catch((error: Error) => {
        this.logError(error,
          'Error while atributing ASIN for Search',
          `${LOGGING_PREFIX}::JSError::SearchAttribution`)
      })
  }

  fetchEnrichments({ nonPlayableIDs, playableIDs }: { nonPlayableIDs?: string[], playableIDs?: string[] }) {
    if ((nonPlayableIDs && nonPlayableIDs.length > 0) || (playableIDs && playableIDs.length > 0)) {
      const nonPlayableStr = nonPlayableIDs
        ? nonPlayableIDs.map(i => `titleIDs[]=${i}`).join('&') : ''
      const playableStr = playableIDs ? playableIDs.map(i => `pTitleIDs[]=${i}`).join('&') : ''

      const queryString = [nonPlayableStr, playableStr].filter(i => i).join('&')
      const url = `${constants.ENRICH_API_ENDPOINT}?${queryString}`

      return fetch(url, this.isUsingMock())
    }
    return Promise.resolve({})
  }

  enrichSingleItem(titleId: string, isPlayable = false) {
    const fetchPromise = isPlayable
      ? this.fetchEnrichments({ nonPlayableIDs: [], playableIDs: [titleId] })
      : this.fetchEnrichments({ nonPlayableIDs: [titleId], playableIDs: [] })

    return fetchPromise.then((data: { [key: string]: any }) => {
      if (data.enrichments.hasOwnProperty(titleId)) {
        this.itemsPlayableStatus = {
          ...this.itemsPlayableStatus,
          [titleId]: data.enrichments[titleId]
        }
      }
      return new Promise((res) => this.setState(this.getResponseUpdate(), res))
    })
  }

  isItemEnrichable(item: StorefrontItem, idx: number) {
    return !item.channelId && item.titleID &&
      !this.itemsPlayableStatus[item.titleID] && item.prerolls &&
      (idx <= HORIZONTAL_ENRICH_THRESHOLD)
  }

  itemIsPlayable(item: StorefrontItem) {
    return item.properties && perlBoolean(item.properties.isIdPlayable)
  }

  getEnrichableItemsFromCollection(col: Collection) {
    if (col &&
      col.items &&
      col.items.length > 0) {
      const allItemsToEnrich = col.items
        .filter(
          (item, idx) => item.titleID &&
            this.isItemEnrichable(item, idx)
        )
      const playableIDs = allItemsToEnrich
        .filter(i => this.itemIsPlayable(i))
        .map(i => i.titleID)
      const nonPlayableIDs = allItemsToEnrich
        .filter(i => !this.itemIsPlayable(i))
        .map(i => i.titleID)

      return { nonPlayableIDs, playableIDs }
    }
    return { nonPlayableIDs: [], playableIDs: [] }
  }

  canLazyLoad() {
    const isLazyEnabled = this.state &&
      this.state.featureSwitches &&
      this.state.featureSwitches.lazyloadPB
    const hasCollections = this.state &&
      this.state.collections

    return Boolean(hasCollections && (isLazyEnabled || this.isUsingMock()))
  }

  refreshItem(playableTitleId: string) {
    let titleIDsToUpdate = Object.keys(this.itemsPlayableStatus)
      .filter(i => this.itemsPlayableStatus[i] &&
        this.itemsPlayableStatus[i].playbackAction &&
        this.itemsPlayableStatus[i].playbackAction.titleID === playableTitleId)

    if (!titleIDsToUpdate.length) {
      titleIDsToUpdate = [playableTitleId]
    }

    // use non playable IDs here to force a call to CCTS - current episode etc may have changed
    return this.fetchEnrichments({ nonPlayableIDs: titleIDsToUpdate }).then((data: { [key: string]: any }) => {
      titleIDsToUpdate.map(titleId => {
        if (data.enrichments.hasOwnProperty(titleId)) {
          this.itemsPlayableStatus = {
            ...this.itemsPlayableStatus,
            [titleId]: data.enrichments[titleId]
          }
        }
      })
      return this.getResponseUpdate()
    })
  }

  addWatchnextItemsToPlayableItems() {
    this.itemsPlayableStatus = {
      ...this.itemsPlayableStatus, ...this.state.collections
        .filter(_ => _.edit) // Only the watchnext carousel is editable
        .map(collection => collection.items)
        .reduce((acc, items) => acc.concat(items), [])
        .filter(item => item.playbackAction)
        .reduce((acc: { [key: string]: any }, item) => {
          acc[item.titleID] = item
          return acc
        }, {})
    }
  }


  enrich() {
    if (!this.state.collections)
      return
    this.addWatchnextItemsToPlayableItems()
    const promises = []
    for (const col of
      this.state.collections.filter((collection) => collection.collectionType === 'SuperHero')) {
      const itemsToUpdate = this.getEnrichableItemsFromCollection(col)
      promises.push(this.fetchEnrichments(itemsToUpdate).then((data: { [key: string]: any }) => data.enrichments))
    }
    return Promise.all(promises).then(results => {
      const enrichments = { ...Object.assign({}, ...results) }
      for (const item in enrichments) {
        if (enrichments.hasOwnProperty(item)) {
          this.itemsPlayableStatus = {
            ...this.itemsPlayableStatus,
            [item]: enrichments[item]
          }
        }
      }

      this.setState(this.getResponseUpdate())
    })
  }

  getUpdatedItem(item: StorefrontItem): StorefrontStateItem {
    const newItem: any = this.itemsPlayableStatus[item.titleID]
    if (!newItem) {
      return item
    }

    const playbackAction = newItem.playbackAction
    const progress = {
      ...newItem.progress,
      isLive: newItem.progress && perlBoolean(newItem.progress.isLive)
    }

    return {
      ...item,
      playbackAction,
      progress
    }
  }

  getResponseUpdate() {
    return {
      collections: this.state.collections.map(
        collection => collection.items &&
          { ...collection, items: collection.items.map(i => this.getUpdatedItem(i)) }
      ),
      // indicate that we have playback on storefront one way or another
      storefrontPlaybackActive: true
    }
  }

  updateCollection(col: number, newItems: StorefrontItem[]) {
    const collection = this.state.collections[col]
    const newCollection = {
      ...collection,
      items: newItems
    }
    const newCollections = [...this.state.collections.slice(0, col),
      newCollection,
    ...this.state.collections.slice(col + 1, this.state.collections.length)]
    this.setState({ collections: newCollections })
  }

  modifyItem(id: number, col: number, override: { [key: string]: boolean }) {
    const items = this.state.collections[col] && this.state.collections[col].items
    const item = items[id]
    const newItem = {
      ...item,
      ...override
    }
    const newItems = [...items.slice(0, id), newItem, ...items.slice(id + 1, items.length)]
    this.updateCollection(col, newItems)
  }

  removeItem(id: number, col: number) {
    const items = this.state.collections[col] && this.state.collections[col].items
    const item = items[id]
    this.modifyItem(id, col, { isDisabled: true })

    const scope = ''
    const method = 'POST'
    const body = {
      asin: item.edit.titleID,
      token: item.edit.token,
      type: item.edit.listType
    }

    return this.fetch(item.edit.url, scope, method, body).then((response: any) => {
      if (!response || !response.success || response.error) {
        throw new Error('Received an error response')
      }
      const newItem = {
        ...item,
        isRemoved: true,
        isDisabled: true
      }
      const newItems = [...items.slice(0, id),
      response && response.success ? newItem : item,
      ...items.slice(id + 1, items.length)]
      this.updateCollection(col, newItems)
    }).catch((error: Error) => {
      this.logError(error,
        'Error while trying to hide from WatchNext',
        `${LOGGING_PREFIX}::JSError::WatchNextHide`)
    })
  }

  refreshLiveChannels() {
    const apiUrl = getLiveChannelUpdateURL(this.channelItems.map(i => i.channelId))

    this.fetch(apiUrl).then((response: InitialArgs) => {
      const channelIdsMap = response.outputMap
      let stateNeedsUpdate = false

      this.channelItems.map(item => {
        const channelIdItem = channelIdsMap[item.channelId]
        if (channelIdItem) {
          const hasChanged = isChannelUpdated(channelIdItem, item.image.url)
          stateNeedsUpdate = stateNeedsUpdate || hasChanged

          if (hasChanged) {
            item.image.url = channelIdItem.imageURL
            item.title = channelIdItem.title
            item.titleID = channelIdItem.asin
          }
        }
      })

      if (stateNeedsUpdate) {
        this.setState(this.state)
      }
    }).catch((error: Error) => {
      this.logError(error,
        'Error while trying to refresh Live Channels',
        `${LOGGING_PREFIX}::JSError::RefreshChannel`)
    })
  }

  updateChannelItem(channelId: string, params: object) {
    let channelItem = this.channelItems.find(i => i.channelId === channelId)
    if (channelItem) {
      channelItem = Object.assign(channelItem, params)
      this.setState(this.state)
    }
  }

  updateStateWithLivePlayback() {
    if (typeof window !== 'undefined' && typeof window.P !== 'undefined') {
      window.P.when('dv-player-integration').execute((player: any) => {
        player.onCapabilities((capabilitiesWrapper: any) => {
          const livePlaybackCapability = capabilitiesWrapper && capabilitiesWrapper.capabilities &&
            capabilitiesWrapper.capabilities.supportsLive
          this.setState({ canLivePlayback: livePlaybackCapability })
        })
      })
    }
  }

  filterResponse(response: StorefrontState) {
    if(!global.__NATIVEAPP__ || !response.collections || !response.collections.length) {
      return response
    }
    return Object.assign({}, response, { collections: response.collections.filter(collection => collection.collectionType === 'Carousel') })
  }

  render() {
    const response = this.state
    const isPhablet = !response.formFactor || response.formFactor === 'Phone'
    const requiresMinimumPageWidth = this.props.featureSwitches &&
      this.props.featureSwitches.requiresMinimumPageWidth
    const hasExperimentalFilters = this.props.featureSwitches &&
      this.props.featureSwitches.hasExperimentalFilters
    const classNames = cn(styles.Storefront, 'tst-storefront',
      { 'min-width-storefront': requiresMinimumPageWidth },
      { [styles.AltTheme]: this.hasAltTheme }) // Local Theme

    const filteredResponse = this.filterResponse(response)
    return (
      // TODO(AVWebDetailPage-1310): The id is a temporary hack to make the hover work.
      <div className={classNames} id="aiv-cl-main-middle">
        {
          this.isRequestSuccessful()
            ? (<Content
              {...filteredResponse}
              isPhablet={isPhablet}
              canLazyLoad={this.canLazyLoad()}
              showBlueBars={this.state.showBlueBars}
              loadFilteredContent={(id: number, url: string) => this.loadFilteredContent(id, url)}
              loadMoreContent={() => this.loadMoreContent()}
              enrichSingleItem={this.enrichSingleItem}
              removeItem={(idx: number, col: number) => this.removeItem(idx, col)}
              fetch={(url: string, scope?: string, method?: string, body?: any) => this.fetch(url, scope, method, body)}
              updateChannelItem={(channelId: string, params: object) => this.updateChannelItem(channelId, params)}
              hasExperimentalFilters={hasExperimentalFilters}
              epgIngress={!this.isLoadingFilteredContent && this.state.epgIngress}
              formFactor={response.formFactor}
              storefrontReloadPageHandler={this.refreshPage.bind(this)}
            />)
            : (<Fallback {...response} />)
        }
      </div>
    )
  }
}

export default Storefront
